import { useEffect, useState, useRef } from 'react';
import styles from './styles/bathrooms.module.css';
import imageData from '../../data/Services/BathroomsData.json';
import FAB from '../../helpers/fab'

const useSwipe = (onSwipeLeft, onSwipeRight) => {
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const onTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) onSwipeLeft();
      else onSwipeRight();
    }
  };

  return { onTouchStart, onTouchEnd };
};

const Bathrooms = () => {
  const [bathrooms, setBathrooms] = useState([]);
  const [modalState, setModalState] = useState({ bathroomIndex: null, imageIndex: null });

  const groupRefs = useRef([]); // Ref to store references to each bathroom group

  useEffect(() => {
    setBathrooms(imageData);
  }, []);

  useEffect(() => {
    if (groupRefs.current.length === 0) return;

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.visible);
          observer.unobserve(entry.target); // optional: only animate once
        }
      });
    }, { threshold: 0 });

    requestAnimationFrame(() => {
      groupRefs.current.forEach((group) => {
        if (group) observer.observe(group);
      });
    });

    return () => observer.disconnect();
  }, [bathrooms]); // run this when bathrooms have been set

  const openModal = (bathroomIndex, imageIndex) => {
    setModalState({ bathroomIndex, imageIndex });
  };

  const closeModal = () => {
    setModalState({ bathroomIndex: null, imageIndex: null });
  };

  const prevImage = () => {
    setModalState((prev) => {
      if (prev.bathroomIndex === null) return prev;
      const images = bathrooms[prev.bathroomIndex].images;
      const newIndex = prev.imageIndex === 0 ? images.length - 1 : prev.imageIndex - 1;
      return { ...prev, imageIndex: newIndex };
    });
  };

  const nextImage = () => {
    setModalState((prev) => {
      if (prev.bathroomIndex === null) return prev;
      const images = bathrooms[prev.bathroomIndex].images;
      const newIndex = (prev.imageIndex + 1) % images.length;
      return { ...prev, imageIndex: newIndex };
    });
  };
  
  // Add state to track the mouse position
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [zoomScale, setZoomScale] = useState(1);  // Default scale is 1
  const imageWrapperRef = useRef(null);

  // Mouse move handler to track mouse position inside the image wrapper
  const handleMouseMove = (e) => {
    if (imageWrapperRef.current) {
      const { left, top, width, height } = imageWrapperRef.current.getBoundingClientRect();
      const x = ((e.clientX - left) / width) * 100;
      const y = ((e.clientY - top) / height) * 100;
      setMousePos({ x, y });
      setZoomScale(1.5);  // Apply zoom scale when mouse moves
    }
  };

  // Reset zoom when mouse leaves the image
  const handleMouseLeave = () => {
    setZoomScale(1);  // Reset to normal scale
  };

  const isModalOpen = modalState.bathroomIndex !== null;
  const swipeHandlers = useSwipe(nextImage, prevImage);

  return (
    <div className={styles.servicePage}>
      <section className={styles.bathroomHero}>
        <img src="https://ik.imagekit.io/1stopkitchens/images/services/bathrooms/background.png" style={{display: 'none'}} alt="preload" loading="eager" /> {/* Added for preload */}
        <div className={styles.bathroomHeroOverlay} />
        <h1 className={styles.bathroomHeroTitle}>Bathrooms</h1>
      </section>

      <div className={styles.bathroomContent}>
        {/* Info boxes */}
        <div className={styles.infoBoxes}>
          <div className={styles.infoBox}>
            <h2>Vanity Design & Installation</h2>
            {/* <p>We custom-build vanities that add beauty and function to your bathroom space.</p> */}
            <p>We custom-build vanities designed to enhance your bathroom’s beauty and functionality, tailored to fit your unique space.</p>
          </div>
          <div className={styles.infoBox}>
            <h2>Your Bathroom, Your Style</h2> 
            {/* <p>All the styles, colours, and finishes we offer for kitchens are also available for bathrooms, so your vision can carry through every room.</p> */}
            {/* <p>The same styles, colours, and finishes we offer for kitchens are available for your bathroom too.</p> */}
            <p>We bring the same wide range of styles, colours, and finishes from our kitchen designs to your bathroom, so your vision flows seamlessly.</p>
          </div>
          <div className={styles.infoBox}>
            <h2>Full Renovation Support</h2>
            {/* <p>From demolition to finishing touches, we can manage the entire renovation process.</p> */}
            <p>From demolition to finishing touches, we manage the entire renovation process with care, ensuring every detail aligns with your vision.</p>
          </div>
        </div>

        {/* Bathroom groups */}
        {bathrooms.map((bathroom, bathroomIndex) => {
          const images = bathroom.images;
          const featuredImageIndex = images.findIndex(i => i.isFeatured === true);
          const featureImage = featuredImageIndex !== -1 ? images[featuredImageIndex] : images[0];

          const hasMultipleImages = images.length > 1;
          const thumbnails = images.slice(0, 4);
          const remainingCount = images.length - 4;

          return (
            <section 
              key={bathroomIndex} 
              ref={(el) => groupRefs.current[bathroomIndex] = el} 
              className={styles.bathroomGroup} // Initially hidden
            >
              <div className={styles.topBorder} />
              <div className={styles.bathroomRow}>
                <div className={`${styles.featureImage} ${!hasMultipleImages ? styles.singleImage : ''}`}>
                  <img
                    src={featureImage.src}
                    loading="lazy"
                    srcSet={`${featureImage.src}?tr=w-600,h-600,cm-extract,ar-16-9 600w,
                              ${featureImage.src}?tr=w-1200,h-1200,cm-extract,ar-16-9 1200w,
                              ${featureImage.src}?tr=w-1800,h-1800,cm-extract,ar-16-9 1800w`}
                    alt={featureImage.alt}
                    onClick={() => openModal(bathroomIndex, featuredImageIndex)}
                  />
                </div>

                {hasMultipleImages && (
                  <div className={styles.thumbnailColumn}>
                    {thumbnails.map((img, index) => {
                      if (img !== featureImage) {
                        const imageIndex = index;
                        const isLastVisible = index === thumbnails.length - 1 && remainingCount > 0;

                        return (
                          <div
                            key={img.id}
                            className={styles.thumbItem}
                            onClick={() => openModal(bathroomIndex, imageIndex)}
                          >
                            <img 
                              src={img.src} 
                              alt={img.alt}
                              loading="lazy"
                              srcSet={`${img.src}?tr=w-600,h-600,cm-extract,ar-16-9 600w,
                                      ${img.src}?tr=w-1200,h-1200,cm-extract,ar-16-9 1200w,
                                      ${img.src}?tr=w-1800,h-1800,cm-extract,ar-16-9 1800w`}
                            />
                            {isLastVisible && (
                              <div className={styles.overlayMore}>+{remainingCount}</div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal} {...swipeHandlers}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal" title="Close">×</button>

            <div
              className="modal-image-wrapper zoom-view"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              ref={imageWrapperRef}
            >
              <img
                src={bathrooms[modalState.bathroomIndex].images[modalState.imageIndex].src}
                alt={bathrooms[modalState.bathroomIndex].images[modalState.imageIndex].alt}
                className="modal-image"
                style={{
                  transformOrigin: `${mousePos.x}% ${mousePos.y}%`,
                  transform: zoomScale ? `scale(${zoomScale})` : 'scale(1)',
                  transition: 'transform 0.2s ease-out',
                }}
              />
            </div>

            <div className="modal-thumbnails">
              {bathrooms[modalState.bathroomIndex].images.map((img, index) => (
                <img
                  key={img.id}
                  src={img.src}
                  alt={img.alt}
                  className={`thumbnail-image ${index === modalState.imageIndex ? 'active' : ''}`}
                  onClick={() => setModalState({ ...modalState, imageIndex: index })}
                />
              ))}
            </div>

            <div className="modal-nav-buttons">
              {bathrooms[modalState.bathroomIndex].images.length > 1 && (
                <>
                  <button className="nav-btn prev-btn" onClick={prevImage} aria-label="Previous image" title="Previous image">‹</button>
                  <button className="nav-btn next-btn" onClick={nextImage} aria-label="Next image" title="Next image">›</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {!isModalOpen && <FAB />}
    </div>
  );
};

export default Bathrooms;